import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { usePersonNameOrder } from '../../hooks/useTableSort';
import { getChrYearly } from '../../api/chrYearly';
import { formatNumber, formatMoney } from '../../utils/format';
import { cmpVal, normalizeName, buildSearchKey } from '../../utils/personTable';
import PageShell from '../../components/PageShell';
import Card from '../../components/Card';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import PersonTh from '../../components/PersonTh';
import InfoTooltip from '../../components/InfoTooltip';
import LcrPlan from './LcrPlan';
import { p, tp, useP } from '../../i18n/useP';
import './HrPage.css';

/* Salaire horaire (LCR) — coût salarial chargé par heure travaillée disponible
   (OT), brique de base du coût horaire. Lecture seule : tout est dérivé du moteur
   CHR (contrats + extras + LPP + charges sociales), endpoint /chr.
   Gabarit : PageShell + <LcrPlan> (le plan de calcul, qui a remplacé la card
   « Synthèse » le 2026-08-21) + Card (table). Le plan pilote le FOCUS : la
   table se réduit aux colonnes de l'étape choisie. */

/* FOCUS (2026-08-21) — chaque tuile du plan de calcul réduit le tableau à ses
   colonnes. Le libellé et le département restent toujours là : ils identifient
   la ligne, ils ne font pas partie du calcul. */
const FOCUS = {
  rem:   ['fte_pct', 'base_salary', 'annual_extras', 'gross_salary'],
  /* Le focus CS montre CE QUI FAIT LA SOMME : les quatre TAUX d'abord, puis
     les quatre MONTANTS dans le même ordre (2026-08-23). En alternant taux et
     montant, l'œil devait changer d'unité à chaque colonne ; groupés, chaque
     bloc se lit d'un seul balayage. Sans la rémunération : elle a sa tuile. */
  cs:    ['cs_base_rate_pct', 'cs_ins_rate_pct', 'lpp_empr_pct', 'cs_total_rate_pct',
          'cs_base_chf', 'cs_ins_chf', 'lpp_empr_chf', 'social_charges_empr'],
  masse: ['gross_salary', 'social_charges_empr', 'loaded_payroll'],
  // TT et vacances vivent DANS l'étape TO : la dérivation (TT − vacances)
  // n'est plus une tuile, mais son détail reste à un clic.
  /* Le taux d'activité PRÉCÈDE l'EPT : l'EPT est ce taux ramené à l'année,
     et sans le premier le second reste inexplicable (Guerrero à 100 % sur
     9 mois donne 75 % d'EPT). */
  to:    ['taux_occupation', 'fte_pct', 'tt', 'holidays_hours', 'heures_contractuelles'],
  lcr:   ['loaded_payroll', 'heures_contractuelles', 'lcr'],
};
const IDENTITY = ['person', 'department'];

const COLS = [
  { key: 'person',                label: p('Collaborateur'),          align: 'left' },
  { key: 'department',            label: tp('phrases:p_3d7c87c2af'),             align: 'left' },
  { key: 'taux_occupation',       label: p('Taux d’act.'),            align: 'right', title: p('Taux du contrat en cours ; les mois travaillés sont indiqués quand l’année est partielle') },
  { key: 'fte_pct',               label: p('EPT %'),                  align: 'right', title: tp('phrases:p_b74cd5a17f') },
  { key: 'base_salary',           label: p('Salaire de base'),        align: 'right', money: true, title: p('Salaire de base annuel (hors extras), assiette CS') },
  { key: 'annual_extras',         label: p('Extras annuels'),         align: 'right', money: true, title: tp('phrases:p_b9a7ff468c') },
  { key: 'gross_salary',          label: p('Rémunération totale'),    align: 'right', money: true, title: p('Salaire de base + extras annuels (assiette CS)') },
  { key: 'cs_empr_rate_pct',      label: p('Taux CS'),                align: 'right', title: p('Taux de charges sociales employeur (hors LPP)') },
  { key: 'cs_empr_chf',           label: p('Charges sociales (hors LPP)'), align: 'right', money: true, title: p('Assiette (salaire brut) × taux CS employeur') },
  { key: 'cs_base_rate_pct',      label: p('Cotisation de base (%)'), align: 'right', title: p('AVS / AI / APG, AC, allocations familiales, frais admin.') },
  { key: 'cs_ins_rate_pct',       label: p('Assurance suppl. (%)'),   align: 'right', title: p('AAP, AANP, LAA complémentaire, indemnités journalières — part employeur') },
  { key: 'lpp_empr_pct',          label: p('LPP (%)'),                align: 'right', title: p('Part employeur LPP en % de la rémunération') },
  { key: 'cs_total_rate_pct',     label: p('Total ch. sociales (%)'), align: 'right', strong: true, title: p('Cotisation de base + assurances + LPP') },
  { key: 'cs_base_chf',           label: p('Cotisation de base'),     align: 'right', money: true, title: p('Rémunération × taux de cotisation de base') },
  { key: 'cs_ins_chf',            label: p('Assurance suppl.'),       align: 'right', money: true, title: p('Solde de la cotisation hors LPP') },
  { key: 'lpp_empr_chf',          label: p('LPP'),                    align: 'right', money: true, title: p('Cotisation LPP part employeur') },
  { key: 'social_charges_empr',   label: p('Total ch. sociales'),     align: 'right', money: true, strong: true, title: p('Cotisation de base + assurances + LPP') },
  { key: 'loaded_payroll',        label: tp('phrases:p_a2b47f39e5'),         align: 'right', money: true, title: p('Salaire brut + CS empr. (= numérateur du LCR)') },
  // Temps total = heures contractuelles + vacances (2026-08-21) : le plan de
  // calcul montre le passage TT → TO, il lui faut donc les deux bouts.
  { key: 'tt',                    label: p('Temps total (TT)'),       align: 'right', title: p('Heures contractuelles + vacances') },
  { key: 'holidays_hours',        label: p('Vacances'),               align: 'right', title: p('Heures de vacances déduites du temps total') },
  { key: 'heures_contractuelles', label: tp('phrases:p_dcf6e0e541'), align: 'right', title: tp('phrases:p_084f7110a7') },
  { key: 'lcr',                   label: p('Salaire horaire (LCR)'),  align: 'right', title: tp('phrases:p_ac2cbd5fd2'), accent: true },
];

export default function LabourCostRatePage({ activeTenant, year, embedded, unit = 'chf', focus }) {
  const { t: i18nT } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = usePersistentValue(`lcr_sort_col_${tenantId}`, 'last_name_sort');
  // Ordre d'affichage du nom : « NOM Prénom » (défaut) ou « Prénom NOM ».
  // Le mini-bouton ⇄ de l'en-tête inverse ; le tri de la colonne suit ce qui
  // s'affiche en premier.
  const { nameOrder, flipNameOrder } = usePersonNameOrder(`lcr_name_order_${tenantId}`, sortCol, setSortCol);
  const [sortDir, setSortDir] = usePersistentValue(`lcr_sort_dir_${tenantId}`, 'asc');
  const [search, setSearch] = useState('');
  // « Actif » = statut backend (contrat ouvert à ce jour) — le backend exclut déjà
  // les personnes sans contrat sur l'année ; ce toggle masque en plus les départs.
  const [status, setStatus] = usePersistentValue(`lcr_status_${tenantId}`, 'all');

  useEffect(() => {
    if (!tenantId) { setDetail(null); return; }
    setLoading(true); setError(null);
    getChrYearly(tenantId, year)
      .then(r => r.error ? setError(r.error) : setDetail(r.data))
      .catch(e => setError(e.message || p('Erreur réseau')))
      .finally(() => setLoading(false));
  }, [tenantId, year]);

  const rows = useMemo(() => (detail?.rows || []).map(r => ({
    ...r,
    person_name: `${r.last_name || ''} ${r.first_name || ''}`.trim(),
    first_name_sort: normalizeName(r.first_name),
    last_name_sort: normalizeName(r.last_name),
    _search: buildSearchKey(r.first_name, r.last_name, r.department),
  })), [detail]);

  const viewRows = useMemo(() => {
    const q = normalizeName(search.trim());
    const base = rows.filter(r => {
      if (status === 'active' && !(r.status === 'active' || r.status === 'joined')) return false;
      if (q && !r._search.includes(q)) return false;
      return true;
    });
    base.sort((a, b) => { const c = cmpVal(a[sortCol], b[sortCol]); return sortDir === 'asc' ? c : -c; });
    return base;
  }, [rows, search, status, sortCol, sortDir]);

  /* `focus` fige l'étape depuis l'extérieur : l'export Excel monte la page une
     fois par tuile du plan de calcul, pour sortir une feuille par étape (le
     tableau se réduit aux colonnes de l'étape choisie). À l'écran, la prop est
     absente et l'étape reste pilotée par le clic. */
  const [step, setStep] = useState(focus || 'lcr');
  const effStep = focus || step;

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  /* Le pied SOMME LES LIGNES AFFICHÉES — pas les totaux serveur. Ceux-ci
     couvrent toute l'année (personnes parties comprises) : sous un filtre
     « Actifs », le pied annonçait « 18 pers. » avec les heures de 20 —
     constat du 2026-08-21 (TT 32'892 affiché pour 29'296 de lignes). Les
     totaux serveur restent la vérité du PLAN au-dessus, qui couvre l'année
     entière et le dit (« · N personnes »). */
  const vis = useMemo(() => {
    const sum = (key) => viewRows.reduce((a, r) => a + (Number(r[key]) || 0), 0);
    return {
      fte: viewRows.reduce((a, r) => a + (Number(r.fte_pct) || 0) / 100, 0),
      base_salary: sum('base_salary'),
      annual_extras: sum('annual_extras'),
      gross_salary: sum('gross_salary'),
      cs_empr_chf: sum('cs_empr_chf'),
      cs_base_chf: sum('cs_base_chf'),
      cs_ins_chf: sum('cs_ins_chf'),
      lpp_empr_chf: sum('lpp_empr_chf'),
      social_charges_empr: sum('social_charges_empr'),
      loaded_payroll: sum('loaded_payroll'),
      holidays: sum('holidays_hours'),
      to: sum('heures_contractuelles'),
    };
  }, [viewRows]);

  if (!tenantId) return <PageShell embedded={embedded} title={p('Salaire horaire (LCR)')}><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></PageShell>;

  // Totaux SERVEUR : la vérité du PLAN (année entière, personnes parties
  // comprises) — le pied du tableau, lui, somme les lignes affichées (`vis`).
  const tot = detail?.totals;
  const k = detail?.kpis;
  const visKeys = new Set([...IDENTITY, ...(FOCUS[effStep] || FOCUS.lcr)]);
  const cols = COLS.filter((c) => visKeys.has(c.key));
  const fmt0 = (v) => formatNumber(v, 0);          // heures / nombres (jamais scalés)
  const fmt2 = (v) => formatNumber(v, 2);
  const mny = (v) => formatMoney(v, unit, 0);      // montants (÷1000 si kCHF)
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF'; // le LCR (CHF/h) reste en CHF

  /* Rendu d'une cellule et de son total — porté par la COLONNE et non par
     l'ordre du JSX : le tableau se réduit aux colonnes de l'étape choisie,
     l'ordre des <td> doit donc suivre `cols`, pas une liste écrite en dur. */
  const ttOf = (x) => (Number(x.heures_contractuelles) || 0) + (Number(x.holidays_hours) || 0);
  const CELL = {
    person:      (r) => <td key="person"><PersonName firstName={r.first_name} lastName={r.last_name} order={nameOrder} /></td>,
    department:  (r) => <td key="department" className={r.department ? '' : 'hr-page__muted'}>{r.department || '—'}</td>,
    fte_pct:     (r) => <td key="fte_pct" className="hr-page__num">{formatNumber(r.fte_pct, 1)}%</td>,
    /* Taux du contrat en cours, suivi des mois travaillés quand l'année est
       partielle : c'est le seul moyen de lire l'EPT voisin sans le recalculer
       de tête. Douze mois ne se précisent pas — c'est le cas normal. */
    taux_occupation: (r) => (
      <td key="taux_occupation" className="hr-page__num">
        {formatNumber(r.taux_occupation, 1)}%
        {r.months_in_year < 12 && (
          <span className="hr-page__muted"> · {formatNumber(r.months_in_year, 0)} {p('mois')}</span>
        )}
      </td>
    ),
    base_salary:         (r) => <td key="base_salary" className="hr-page__num">{mny(r.base_salary)}</td>,
    annual_extras:       (r) => <td key="annual_extras" className="hr-page__num">{mny(r.annual_extras)}</td>,
    gross_salary:        (r) => <td key="gross_salary" className="hr-page__num">{mny(r.gross_salary)}</td>,
    cs_empr_rate_pct:    (r) => <td key="cs_empr_rate_pct" className="hr-page__num">{formatNumber(r.cs_empr_rate_pct, 1)}%</td>,
    /* Un contrat qui porte son PROPRE taux ne dit pas comment il se répartit :
       la case reste vide plutôt que de recevoir une clé inventée. */
    cs_base_rate_pct:    (r) => <td key="cs_base_rate_pct" className={`hr-page__num${r.cs_base_rate_pct == null ? ' hr-page__muted' : ''}`}>{r.cs_base_rate_pct == null ? '—' : `${formatNumber(r.cs_base_rate_pct, 1)}%`}</td>,
    cs_ins_rate_pct:     (r) => <td key="cs_ins_rate_pct" className={`hr-page__num${r.cs_ins_rate_pct == null ? ' hr-page__muted' : ''}`}>{r.cs_ins_rate_pct == null ? '—' : `${formatNumber(r.cs_ins_rate_pct, 1)}%`}</td>,
    cs_total_rate_pct:   (r) => <td key="cs_total_rate_pct" className="hr-page__num"><strong>{formatNumber(r.cs_total_rate_pct, 1)}%</strong></td>,
    cs_base_chf:         (r) => <td key="cs_base_chf" className={`hr-page__num${r.cs_base_chf == null ? ' hr-page__muted' : ''}`}>{r.cs_base_chf == null ? '—' : mny(r.cs_base_chf)}</td>,
    cs_ins_chf:          (r) => <td key="cs_ins_chf" className={`hr-page__num${r.cs_ins_chf == null ? ' hr-page__muted' : ''}`}>{r.cs_ins_chf == null ? '—' : mny(r.cs_ins_chf)}</td>,
    cs_empr_chf:         (r) => <td key="cs_empr_chf" className="hr-page__num">{mny(r.cs_empr_chf)}</td>,
    lpp_empr_pct:        (r) => <td key="lpp_empr_pct" className="hr-page__num">{formatNumber(r.lpp_empr_pct, 1)}%</td>,
    lpp_empr_chf:        (r) => <td key="lpp_empr_chf" className="hr-page__num">{mny(r.lpp_empr_chf)}</td>,
    social_charges_empr: (r) => <td key="social_charges_empr" className="hr-page__num"><strong>{mny(r.social_charges_empr)}</strong></td>,
    loaded_payroll:      (r) => <td key="loaded_payroll" className="hr-page__num">{mny(r.loaded_payroll)}</td>,
    tt:                  (r) => <td key="tt" className="hr-page__num">{fmt0(ttOf(r))}</td>,
    holidays_hours:      (r) => <td key="holidays_hours" className="hr-page__num">{fmt0(r.holidays_hours)}</td>,
    heures_contractuelles: (r) => <td key="heures_contractuelles" className="hr-page__num">{fmt0(r.heures_contractuelles)}</td>,
    lcr:                 (r) => <td key="lcr" className="hr-page__num hr-page__col-accent hr-page__col-sticky-right"><strong>{fmt2(r.lcr)}</strong></td>,
  };
  const cell = (c, r) => CELL[c.key](r);

  const FOOT = {
    taux_occupation:     () => '—',
    fte_pct:             () => formatNumber(vis.fte, 1),
    base_salary:         () => mny(vis.base_salary),
    annual_extras:       () => mny(vis.annual_extras),
    gross_salary:        () => mny(vis.gross_salary),
    // Taux du pied = taux MOYEN PONDÉRÉ (Σ CHF ÷ Σ assiette), pas la moyenne
    // des taux — c'est le seul qui se réconcilie avec la colonne des montants.
    cs_empr_rate_pct:    () => (vis.gross_salary > 0 ? `${formatNumber(vis.cs_empr_chf / vis.gross_salary * 100, 1)}%` : '—'),
    cs_empr_chf:         () => mny(vis.cs_empr_chf),
    // Taux du pied = taux MOYEN PONDÉRÉ (Σ CHF ÷ Σ assiette), jamais la moyenne
    // des taux : c'est le seul qui se réconcilie avec la colonne des montants.
    cs_base_rate_pct:    () => (vis.gross_salary > 0 ? `${formatNumber(vis.cs_base_chf / vis.gross_salary * 100, 1)}%` : '—'),
    cs_ins_rate_pct:     () => (vis.gross_salary > 0 ? `${formatNumber(vis.cs_ins_chf / vis.gross_salary * 100, 1)}%` : '—'),
    cs_total_rate_pct:   () => (vis.gross_salary > 0 ? `${formatNumber(vis.social_charges_empr / vis.gross_salary * 100, 1)}%` : '—'),
    cs_base_chf:         () => mny(vis.cs_base_chf),
    cs_ins_chf:          () => mny(vis.cs_ins_chf),
    lpp_empr_pct:        () => (vis.gross_salary > 0 ? `${formatNumber(vis.lpp_empr_chf / vis.gross_salary * 100, 1)}%` : '—'),
    lpp_empr_chf:        () => mny(vis.lpp_empr_chf),
    social_charges_empr: () => mny(vis.social_charges_empr),
    loaded_payroll:      () => mny(vis.loaded_payroll),
    tt:                  () => fmt0(vis.to + vis.holidays),
    holidays_hours:      () => fmt0(vis.holidays),
    heures_contractuelles: () => fmt0(vis.to),
    // LCR du pied = Σ masse chargée affichée ÷ Σ TO affiché (même règle).
    lcr:                 () => (vis.to > 0 ? fmt2(vis.loaded_payroll / vis.to) : '—'),
  };
  const foot = (c) => (
    <td key={c.key}
        className={`hr-page__num${c.accent ? ' hr-page__col-accent hr-page__col-sticky-right' : ''}`}>
      <strong>{FOOT[c.key]()}</strong>
    </td>
  );

  /* Tri d'en-tête : on presse LA COLONNE, toutes pareil. La colonne
     Collaborateur passe par l'en-tête standard <PersonTh> (bascule ⇄ nom /
     prénom, tri sur ce qui s'affiche en premier) — ce qui s'est inventé ici
     vaut pour toutes les tables de personnes. */
  function Th({ col }) {
    if (col.key === 'person') {
      return (
        <PersonTh
          sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
          nameOrder={nameOrder} onFlip={flipNameOrder}
          className="hr-page__sort-th hr-page__th-center"
          style={{ textAlign: col.align, verticalAlign: 'top' }}
        />
      );
    }
    const isSorted = sortCol === col.key;
    return (
      <th className={`hr-page__sort-th hr-page__th-center${isSorted ? ' is-sorted' : ''}${col.accent ? ' hr-page__col-accent hr-page__col-sticky-right' : ''}`}
          style={{ textAlign: col.align, verticalAlign: 'top' }}
          onClick={() => handleSort(col.key)}
          title={col.title || p('Cliquer pour trier')}>
        {/* L'unité suit la bascule CHF/kCHF : elle ne peut pas être écrite
            dans le libellé, sinon elle mentirait la moitié du temps. */}
        <span className={col.strong ? 'hr-th-strong' : undefined}>
          {col.label}{col.money ? ` (${chfLbl})` : ''}
        </span>
        {col.key === 'lcr' && <InfoTooltip term="lcr" align="right" />}
        {isSorted && <span className="hr-page__sort-ind">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </th>
    );
  }

  /* Entête standard de carte (2026-09-01) : filtres derrière l'entonnoir,
     recherche en dernier. */
  const nFilters = (status === 'active' ? 1 : 0) + (search.trim() ? 1 : 0);
  const stdFilters = (
    <>
      <FilterIcon />
      <SegmentedToggle
        compact
        label={p('Statut')} value={status} onChange={setStatus}
        options={[
          { value: 'all', label: p('Tous') },
          { value: 'active', label: p('Actifs'), tone: 'alert' },
        ]}
      />
      <FilterCount count={nFilters} onClear={() => { setStatus('all'); setSearch(''); }} />
    </>
  );

  return (
    <PageShell embedded={embedded} title={p('Salaire horaire (LCR)')} className="lcr-page">
      {loading && <div className="hr-page__loading">{i18nT('common:status.loading')}</div>}
      {error && <div className="hr-modal__error" style={{ marginBottom: 14 }}>{error}</div>}

      {!loading && detail && tot && k && (
        <>
          <LcrPlan totals={tot} kpis={k} unit={unit} year={detail.year} step={effStep} onStep={setStep} />

          <CardShell
            title={<>{p('Salaire horaire par personne')} <small>· {viewRows.length} / {rows.length}</small></>}
            filters={stdFilters}
            search={<SearchField value={search} onChange={setSearch} placeholder={p('Rechercher…')} />}
            filterCount={nFilters}
            collapsible storageKey="lcr-per-person"
            flush
          >
            <div className="hr-page__table-wrap">
              <table className="hr-page__table">
                <thead>
                  <tr>{cols.map(col => <Th key={col.key} col={col} />)}</tr>
                </thead>
                <tbody>
                  {viewRows.map(r => (
                    <tr key={r.person_id}>{cols.map(c => cell(c, r))}</tr>
                  ))}
                  {viewRows.length === 0 && (
                    <tr><td colSpan={cols.length} className="hr-page__empty">{p('Aucune personne active sur')} {detail.year}.</td></tr>
                  )}
                </tbody>
                {viewRows.length > 1 && (
                  <tfoot>
                    <tr className="hr-page__total-row">
                      <td colSpan={2}><strong>{p('Total / Moyenne')}</strong> · {viewRows.length} {p('pers.')}</td>
                      {cols.filter(c => !IDENTITY.includes(c.key)).map(c => foot(c))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardShell>
        </>
      )}
    </PageShell>
  );
}
